import { theme, Spinner, Card, Icon, IconDictionary } from '@ketch-com/deck'
import { Box, Typography, Divider } from '@mui/material'
import { useDataSentryContext } from 'pages/dataSentry/context/DataSentryContext'
import pluralize from 'pluralize'
import { ClassificationType } from '@ketch-com/windlass/dist/dataSentry/v1/events/events'
import { classificationTypeTitles, enumToLabelChipVariantMap } from 'pages/dataSentry/constants'
import { AssetsTabTableLabelChip } from 'pages/dataSystems/DataSystem/assetsTab/components/AssetsTabTableLabelChip'
import { useEventsBySystem } from 'api/dataSentry/queries/useDataSentryEventsBySystem'
import { TopDataCollectingSystemItem } from 'pages/dataSentry/systems/systemsTabComponents/TopDataCollectingSystemItem'
import { formatToK } from 'pages/dataSentry/utils/transformPieChartData'
import { useDataSentryEventsList } from 'api/dataSentry/queries/useDataSentryEventsList'

export const Overview: React.FC = () => {
  const {
    isClassificationDetailsLoading,
    classificationDetails,
    dataClassificationDetails,
    selectedTimeRange,
    selectedProperty,
    dataTypesTotalCount,
    systemsSummary,
    eventsSummary,
    selectedPropertyCode,
    hasPropertyAndScanTimeRange,
  } = useDataSentryContext()

  const { data: eventsList, isLoading: isLoadingEventsList } = useDataSentryEventsList({
    params: {
      'start-date': selectedTimeRange?.startDate,
      'end-date': selectedTimeRange?.endDate,
      'property-codes': selectedPropertyCode,
      'classification-type': dataClassificationDetails?.type,
      'classification-code': dataClassificationDetails?.code,
    },
    enabled: hasPropertyAndScanTimeRange,
  })

  const { data: topSystems, isLoading: isTopSystemsLoading } = useEventsBySystem({
    params: {
      'start-date': selectedTimeRange?.startDate,
      'end-date': selectedTimeRange?.endDate,
      'property-codes': selectedProperty?.code,
      'classification-type': dataClassificationDetails?.type,
      'classification-code': dataClassificationDetails?.code,
      limit: 3,
    },
    enabled: hasPropertyAndScanTimeRange,
  })

  const displayedClassificationType =
    dataClassificationDetails?.type === ClassificationType.DATA_CATEGORY
      ? ClassificationType.DATA_TYPE
      : ClassificationType.DATA_CATEGORY
  let classificationType = classificationTypeTitles[ClassificationType.UNSPECIFIED]
  if (dataClassificationDetails?.type === ClassificationType.DATA_CATEGORY) {
    classificationType = classificationTypeTitles[ClassificationType.DATA_TYPE]
  } else if (dataClassificationDetails?.type === ClassificationType.DATA_TYPE) {
    classificationType = classificationTypeTitles[ClassificationType.DATA_CATEGORY]
  }

  const systemPercentage =
    topSystems?.total && systemsSummary?.total ? Math.round((topSystems.total / systemsSummary.total) * 100) : 0

  const totalClassificationSystems = formatToK(systemsSummary?.total || 0)

  const networkPercentage =
    eventsList?.total && eventsSummary?.total ? Math.round((eventsList.total / eventsSummary?.total) * 100) : 0

  const totalNetworkRequests = formatToK(eventsSummary?.total || 0)

  const associatedTypes = classificationDetails?.dependencies
    ?.map(dependency => {
      if (dependency.classificationType !== displayedClassificationType) return null
      return dependency.classifications?.map(classification => {
        return (
          <AssetsTabTableLabelChip
            key={classification.code}
            title={classification?.name || classification?.code}
            variant={enumToLabelChipVariantMap[dependency?.classificationType || ClassificationType.UNSPECIFIED]}
            hideConfidence
            hideIcon
            isViewOnly
            supportMultiLineTitle
          />
        )
      })
    })
    .filter(Boolean)

  if (isClassificationDetailsLoading || isTopSystemsLoading || isLoadingEventsList) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" flexBasis={300}>
        <Spinner size={32} thickness={2.5} />
      </Box>
    )
  }

  return (
    <Box display="flex" flexDirection="column" gap={3} height="65vh" overflow={'auto'}>
      <Box display="flex" gap={2}>
        <Box flex={1} display="flex" flexDirection="column" gap={2}>
          <Typography variant="label" color={theme.palette.Text.Secondary}>
            About
          </Typography>
          <Typography variant="body" color={theme.palette.Text.Primary}>
            {classificationDetails?.classification?.description || 'No description available.'}
          </Typography>
        </Box>
        <Box flex={1} display="flex" flexDirection="column" gap={1}>
          <Typography variant="label" color={theme.palette.Text.Secondary}>
            {pluralize(classificationType, associatedTypes?.length)} Associated
          </Typography>
          <Box display="flex" flexWrap="wrap" gap={1}>
            {associatedTypes}
          </Box>
        </Box>
      </Box>
      <Divider />
      <Box display="flex" gap={2}>
        <Box flex={1} display="flex" flexDirection="column" gap={2}>
          <Typography variant="label" color={theme.palette.Text.Secondary}>
            Top 3 Systems Collecting
          </Typography>
          <Box display="flex" flexWrap="wrap" justifyContent="space-evenly">
            {topSystems?.dataSystems?.map((system, index) => {
              const dataTypes =
                system.classificationsByType?.find(cls => cls.classificationType === ClassificationType.DATA_TYPE)
                  ?.classifications || []
              return (
                <TopDataCollectingSystemItem
                  key={system.dataSystem?.code}
                  rank={index + 1}
                  name={system.dataSystem?.name}
                  code={system.dataSystem?.code}
                  logo={system.dataSystem?.logoUrl}
                  collectionEvents={system.count}
                  dataTypeCount={dataTypes.length}
                  trackerCount={system.trackerCount}
                  hasData={true}
                  totalDataTypeCount={dataTypesTotalCount}
                />
              )
            })}
          </Box>
        </Box>

        <Box flex={1} display="flex" flexDirection="column" gap={2}>
          <Typography variant="label" color={theme.palette.Text.Secondary}>
            Collected By
          </Typography>
          <Box display="flex" flexWrap="wrap" gap={2}>
            <Box width={200}>
              <Card
                sx={{
                  border: `1px solid ${theme.palette.Black.o8}`,
                  backgroundColor: theme.palette.White.o100,
                  padding: 2,
                }}
                customContentCard={
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start" width="100%">
                    <Box display="flex" flexDirection="column" gap={1}>
                      <Typography variant="h3">
                        {systemPercentage}%
                        <sup>
                          <Typography variant="smallLabel" color={theme.palette.Text.Secondary}>
                            {totalClassificationSystems}
                          </Typography>
                        </sup>
                      </Typography>

                      <Typography variant="label">Systems</Typography>
                    </Box>
                    <Icon name={IconDictionary.System} width="32px" height="32px" iconColor={theme.palette.Black.o48} />
                  </Box>
                }
              />
            </Box>

            <Box width={200}>
              <Card
                sx={{
                  border: `1px solid ${theme.palette.Black.o8}`,
                  backgroundColor: theme.palette.White.o100,
                  padding: 2,
                }}
                customContentCard={
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start" width="100%">
                    <Box display="flex" flexDirection="column" gap={1}>
                      <Typography variant="h3">
                        {networkPercentage}%
                        <sup>
                          <Typography variant="smallLabel" color={theme.palette.Text.Secondary}>
                            {totalNetworkRequests}
                          </Typography>
                        </sup>
                      </Typography>

                      <Typography variant="label">Network Requests</Typography>
                    </Box>
                    <Icon name={IconDictionary.OSort} width="32px" height="32px" iconColor={theme.palette.Black.o48} />
                  </Box>
                }
              />
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  )
}
